import React from "react";
import { observer } from "mobx-react-lite";
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Grid,
    Typography,
    Paper,
    Chip,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DateTime } from "luxon";
import { Department } from "../../models/Event";
import CartStore from "../../stores/CartStore";
import toast from "../../utils/toast";
import { getErrorMessage } from "../../utils/helpers";

type Props = {
    tournaments: Department[];
    onSelectionComplete: (selectedTournaments: Department[]) => void;
    cartStore: CartStore | null;
};

const TournamentSelection = observer(
    ({ tournaments, onSelectionComplete, cartStore }: Props) => {
        const [selectedTournamentIds, setSelectedTournamentIds] =
            React.useState<Set<number>>(new Set());
        const [loading, setLoading] = React.useState(false);
        const theme = useTheme();
        const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

        const toggleTournament = (tournament: Department) => {
            // Don't allow selection of sold out tournaments
            if (tournament.soldOut) {
                toast.error("This tournament is sold out.");
                return;
            }

            const newSelected = new Set(selectedTournamentIds);
            if (newSelected.has(tournament.id)) {
                newSelected.delete(tournament.id);
            } else {
                newSelected.add(tournament.id);
            }
            setSelectedTournamentIds(newSelected);
        };

        const handleContinue = async () => {
            if (selectedTournamentIds.size === 0) {
                toast.error("Please select at least one tournament.");
                return;
            }

            if (!cartStore) {
                toast.error("Cart not available. Please refresh the page.");
                return;
            }

            setLoading(true);

            try {
                await cartStore.clearCart(false);

                const selectedTournaments = tournaments.filter((t) =>
                    selectedTournamentIds.has(t.id),
                );

                for (const tournament of selectedTournaments) {
                    await cartStore.setGaQuantity(
                        tournament.id,
                        false,
                        1,
                        tournament,
                        undefined,
                        false,
                    );
                }

                onSelectionComplete(selectedTournaments);
                toast.success(
                    `Added ${selectedTournaments.length} tournament${
                        selectedTournaments.length > 1 ? "s" : ""
                    } to cart.`,
                );
            } catch (error) {
                console.error("Error adding tournaments to cart:", error);
                toast.error(getErrorMessage(error));
            } finally {
                setLoading(false);
            }
        };

        // Group tournaments by event date
        const tournamentsByDate = tournaments.reduce((acc, tournament) => {
            const eventDate = DateTime.fromMillis(tournament.to).toFormat(
                "MMMM d, yyyy",
            );
            if (!acc[eventDate]) {
                acc[eventDate] = [];
            }
            acc[eventDate].push(tournament);
            return acc;
        }, {} as Record<string, Department[]>);

        const sortedDates = Object.keys(tournamentsByDate).sort((a, b) => {
            const dateA = DateTime.fromFormat(a, "MMMM d, yyyy");
            const dateB = DateTime.fromFormat(b, "MMMM d, yyyy");
            return dateA.toMillis() - dateB.toMillis();
        });

        return (
            <Box>
                <Paper sx={{ p: 3, mb: 4 }}>
                    <Typography variant="h4" gutterBottom textAlign="center">
                        Tournament Registration 2025
                    </Typography>
                    <Typography
                        variant="body1"
                        textAlign="center"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        Select the tournaments you want to participate in. You
                        can register for multiple tournaments.
                    </Typography>
                    <Typography
                        variant="body2"
                        textAlign="center"
                        sx={{
                            p: 2,
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            borderRadius: 1,
                            fontWeight: 500,
                        }}
                    >
                        Learn more about each tournament in our Discord server:{" "}
                        <a
                            href="https://discord.gg/NXUCqQ9CdA"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: "inherit",
                                textDecoration: "underline",
                                fontWeight: 600,
                            }}
                        >
                            https://discord.gg/NXUCqQ9CdA
                        </a>
                    </Typography>
                </Paper>

                {sortedDates.map((date) => (
                    <Box key={date} sx={{ mb: 4 }}>
                        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                            {date}
                        </Typography>
                        <Grid container spacing={3}>
                            {tournamentsByDate[date]
                                .sort((a, b) => {
                                    const weightA = a.orderWeight || 99;
                                    const weightB = b.orderWeight || 99;

                                    if (weightA !== weightB) {
                                        return weightA - weightB;
                                    }

                                    // If weights are the same, sort by name case-insensitive
                                    return a.name
                                        .toLowerCase()
                                        .localeCompare(b.name.toLowerCase());
                                })
                                .map((tournament) => (
                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                        lg={4}
                                        key={tournament.id}
                                    >
                                        <Card
                                            sx={{
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                border: selectedTournamentIds.has(
                                                    tournament.id,
                                                )
                                                    ? 2
                                                    : 1,
                                                borderColor:
                                                    selectedTournamentIds.has(
                                                        tournament.id,
                                                    )
                                                        ? "primary.main"
                                                        : "divider",
                                                cursor: tournament.soldOut
                                                    ? "not-allowed"
                                                    : "pointer",
                                                opacity: tournament.soldOut
                                                    ? 0.6
                                                    : 1,
                                                transition:
                                                    "all 0.2s ease-in-out",
                                                "&:hover": tournament.soldOut
                                                    ? {}
                                                    : {
                                                          transform:
                                                              "translateY(-2px)",
                                                          boxShadow: 4,
                                                          bgcolor: "grey.50",
                                                      },
                                            }}
                                            onClick={() =>
                                                toggleTournament(tournament)
                                            }
                                        >
                                            <CardContent sx={{ flexGrow: 1 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems:
                                                            "flex-start",
                                                        justifyContent:
                                                            "space-between",
                                                        mb: 2,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="h6"
                                                        component="div"
                                                        sx={{ flexGrow: 1 }}
                                                    >
                                                        {tournament.name}
                                                    </Typography>
                                                    <Checkbox
                                                        checked={selectedTournamentIds.has(
                                                            tournament.id,
                                                        )}
                                                        disabled={
                                                            tournament.soldOut
                                                        }
                                                        onChange={() =>
                                                            toggleTournament(
                                                                tournament,
                                                            )
                                                        }
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                    />
                                                </Box>

                                                {tournament.details && (
                                                    <Box
                                                        sx={{
                                                            bgcolor: "grey.50",
                                                            borderRadius: 2,
                                                            p: 2,
                                                            mb: 2,
                                                            border: "1px solid",
                                                            borderColor:
                                                                "grey.200",
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                "& p": {
                                                                    mb: 1,
                                                                    "&:last-child":
                                                                        {
                                                                            mb: 0,
                                                                        },
                                                                },
                                                                "& a": {
                                                                    color: "primary.main",
                                                                    textDecoration:
                                                                        "none",
                                                                    "&:hover": {
                                                                        textDecoration:
                                                                            "underline",
                                                                    },
                                                                },
                                                                "& strong": {
                                                                    fontWeight: 600,
                                                                },
                                                                "& ul, & ol": {
                                                                    pl: 2,
                                                                    mb: 1,
                                                                },
                                                                "& li": {
                                                                    mb: 0.5,
                                                                },
                                                            }}
                                                            dangerouslySetInnerHTML={{
                                                                __html: tournament.details,
                                                            }}
                                                        />
                                                    </Box>
                                                )}

                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                        gap: 1,
                                                        mb: 2,
                                                    }}
                                                >
                                                    <Chip
                                                        label={`$${(
                                                            tournament.price /
                                                            100
                                                        ).toFixed(2)} + tax`}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                    {tournament.admitCount >
                                                        1 && (
                                                        <Chip
                                                            label={`${tournament.admitCount} people`}
                                                            variant="outlined"
                                                            size="small"
                                                        />
                                                    )}
                                                    {tournament.limitedTicketsAvailable &&
                                                        !tournament.soldOut && (
                                                            <Chip
                                                                label="Limited spots"
                                                                color="warning"
                                                                size="small"
                                                            />
                                                        )}
                                                    {tournament.soldOut && (
                                                        <Chip
                                                            label="SOLD OUT"
                                                            color="error"
                                                            size="small"
                                                            sx={{
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    )}
                                                </Box>

                                                {tournament.performanceDate && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Event Time:{" "}
                                                        {DateTime.fromMillis(
                                                            tournament.performanceDate,
                                                        ).toFormat("h:mm a")}
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                        </Grid>
                    </Box>
                ))}

                {selectedTournamentIds.size > 0 && (
                    <Paper
                        elevation={8}
                        sx={{
                            position: "sticky",
                            bottom: 10,
                            left: 0,
                            right: 0,
                            p: { xs: 1.5, sm: 3 },
                            mt: 4,
                            zIndex: 1100,
                            background:
                                "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(250,250,250,0.98) 100%)",
                            borderTop: "3px solid",
                            borderColor: "primary.main",
                            boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
                        }}
                    >
                        <Box sx={{ maxWidth: "lg", mx: "auto" }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 2,
                                }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            color: "text.primary",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        Selected Tournaments
                                        <Chip
                                            label={selectedTournamentIds.size}
                                            color="primary"
                                            size="small"
                                            sx={{ fontWeight: "bold" }}
                                        />
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 0.5,
                                            maxHeight: {
                                                xs: "60px",
                                                sm: "none",
                                            },
                                            overflowY: {
                                                xs: "auto",
                                                sm: "visible",
                                            },
                                            pr: { xs: 1, sm: 0 },
                                        }}
                                    >
                                        {tournaments
                                            .filter((t) =>
                                                selectedTournamentIds.has(t.id),
                                            )
                                            .map((tournament) => (
                                                <Chip
                                                    key={tournament.id}
                                                    label={tournament.name}
                                                    onDelete={() =>
                                                        toggleTournament(
                                                            tournament,
                                                        )
                                                    }
                                                    color="primary"
                                                    variant="filled"
                                                    size={
                                                        isMobile
                                                            ? "small"
                                                            : "medium"
                                                    }
                                                    sx={{
                                                        "& .MuiChip-deleteIcon":
                                                            {
                                                                color: "primary.contrastText",
                                                                "&:hover": {
                                                                    color: "error.light",
                                                                },
                                                            },
                                                    }}
                                                />
                                            ))}
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: {
                                            xs: "stretch",
                                            sm: "flex-end",
                                        },
                                        gap: 1,
                                        minWidth: { xs: "100%", sm: "auto" },
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            textAlign: {
                                                xs: "center",
                                                sm: "right",
                                            },
                                        }}
                                    >
                                        Total: $
                                        {(
                                            tournaments
                                                .filter((t) =>
                                                    selectedTournamentIds.has(
                                                        t.id,
                                                    ),
                                                )
                                                .reduce(
                                                    (sum, t) => sum + t.price,
                                                    0,
                                                ) / 100
                                        ).toFixed(2)}{" "}
                                        + tax
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size={isMobile ? "medium" : "large"}
                                        onClick={handleContinue}
                                        disabled={loading}
                                        fullWidth
                                        sx={{
                                            minWidth: { xs: "100%", sm: 200 },
                                            py: { xs: 1, sm: 1.5 },
                                            fontSize: {
                                                xs: "0.95rem",
                                                sm: "1.1rem",
                                            },
                                            fontWeight: 600,
                                            boxShadow: 3,
                                            "&:hover": {
                                                boxShadow: 6,
                                                transform: "translateY(-1px)",
                                            },
                                        }}
                                    >
                                        {loading
                                            ? "Adding to Cart..."
                                            : "Continue"}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                )}

                {tournaments.length === 0 && (
                    <Paper sx={{ p: 4, textAlign: "center" }}>
                        <Typography variant="h6" color="text.secondary">
                            No tournaments are currently available for
                            registration.
                        </Typography>
                    </Paper>
                )}
            </Box>
        );
    },
);

export default TournamentSelection;
